import { RoutalPalette } from '@nx-smartmonkey/ui';
import { NodeRendererProps } from 'react-arborist';
import styled from 'styled-components';

import { useCallback } from 'react';
import { TreeApiData } from '..';
import { useRetrieveRightPanelInfo } from '../../../../../../../hooks/right-panel/useRetrieveRightPanelInfo';
import { useRetrieveActivePlanDrivers } from '../../../../hooks/plan-details/useRetrieveActivePlanDrivers';
import { useRetrieveActivePlanStops } from '../../../../hooks/plan-details/useRetrieveActivePlanStops';
import { DriverChip } from './DriverChip';

interface DriverElementStylesProps {
  isSelected?: boolean;
  isDraggingOver?: boolean;
}

const DriverElementStyles = styled.div<DriverElementStylesProps>`
  position: relative;
  background-color: ${({ isSelected }: DriverElementStylesProps) =>
    isSelected ? RoutalPalette.neutral05 : RoutalPalette.neutral00};
  :after {
    content: '';
    position: absolute;
    left: 0;
    width: 100%;
    z-index: 100;
    ${({ isDraggingOver }: DriverElementStylesProps) => {
      if (isDraggingOver) {
        return `
          bottom: -1px;
          height: 2px;
          background-color: ${RoutalPalette.primary20};
        `;
      }

      return `
        bottom: 0;
        height: 1px;
        background-color: ${RoutalPalette.neutral10};
      `;
    }}
  }
`;

interface DriverElementProps extends NodeRendererProps<TreeApiData> {
  selectedStops: string[];
}

export const DriverElement = ({ tree, selectedStops, node, style, dragHandle }: DriverElementProps) => {
  const { map: driversMap } = useRetrieveActivePlanDrivers();
  const { map: stopsMap } = useRetrieveActivePlanStops();
  const { detailId } = useRetrieveRightPanelInfo();

  node.isDraggable = false;

  const driver = driversMap[node.data.id];

  const isExpanded = node.isOpen;

  const isDraggingOver = !driver.is_locked && node.willReceiveDrop;

  const isSelected =
    detailId === node.data.id ||
    selectedStops.some((stopId) => stopsMap[stopId] && stopsMap[stopId].driver_id === node.data.id);

  const toggle = useCallback(() => {
    node.toggle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <DriverElementStyles
      style={{
        ...style,
      }}
      className={`driver-element-${node.rowIndex}`}
      isSelected={isSelected}
      isDraggingOver={isDraggingOver}
      ref={dragHandle}
      onClick={toggle}
    >
      <DriverChip
        driver={driver}
        isFirstInLevel={node.data.isFirstInLevel ?? false}
        isExpanded={isExpanded}
        toggleExpanded={toggle}
      />
    </DriverElementStyles>
  );
};
